// /src/services/auth/magic-links.js
import store from '../../../state/index.js';
import { BaseService } from '../base-service.js';

class MagicLinkService extends BaseService {
  constructor() {
    super();
    this.basePath = '';
    // Removed hardcoded base URL override
    // this.setBaseUrl('https://api.kablewy.ai/v0'); 
  }

  // Debug utility for better error diagnosis
  _debug(label, data) {
    console.group(`MagicLinkService: ${label}`);
    // Check if it's an Error object
    if (data instanceof Error) {
      console.error('Error:', data.message);
      console.error('Stack:', data.stack);
      // Try to parse error message if it looks like JSON
      if (data.message && data.message.startsWith('{') && data.message.endsWith('}')) {
        try {
          console.log('Parsed error:', JSON.parse(data.message));
        } catch (e) {
          // Ignore parse errors
        }
      }
      console.log('Error details:', data);
    } else {
      console.log(data);
    }
    console.groupEnd();
  }

  // Use stored orgId from session if available
  getOrganizationId() {
    try {
      const sessionData = JSON.parse(sessionStorage.getItem('kablewy_session'));
      return sessionData?.orgId || sessionStorage.getItem('lastOrgId');
    } catch {
      return sessionStorage.getItem('lastOrgId');
    }
  }

  // Lookup organization ID by email when not authenticated
  async lookupOrgId(email) {
    try {
      // Normalize email to lowercase for consistency
      const normalizedEmail = email.toLowerCase();
      
      // First check if we already have an orgId stored
      const existingOrgId = this.getOrganizationId();
      if (existingOrgId) {
        return existingOrgId;
      }


      
      const response = await fetch(`${this.baseUrl}/org/lookup?email=${encodeURIComponent(normalizedEmail)}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          console.warn(`No organization found for email: ${normalizedEmail}`);
          return null;
        }
        throw new Error(`Failed to lookup organization: ${response.status}`);
      }
      
      const data = await response.json();
      if (data.found && data.orgId) {
        
        // Store for future use
        sessionStorage.setItem('lastOrgId', data.orgId);
        return data.orgId;
      }
      
      return null;
    } catch (error) {
      console.error('Error looking up organization ID:', error);
      return null;
    }
  }

  async requestLink(email, firstName = '', lastName = '', forceEmail = false) {
    try {
      // Normalize email to lowercase for consistency
      const normalizedEmail = email.toLowerCase();
      
      // Removed logic to get/lookup orgId
      // The backend endpoint /org/magic-links handles lookup
      
      const endpoint = `${this.baseUrl}/org/magic-links`; // Use the global endpoint
      this._debug('requestLink', { endpoint, email: normalizedEmail, firstName, lastName, forceEmail }); // Added debug log
      
      // Check if we have a session token (might still be relevant for some scenarios, keeping it)
      const sessionData = sessionStorage.getItem('kablewy_session');
      let headers = { 'Content-Type': 'application/json' };
      
      if (sessionData) {
        try {
          const parsedSession = JSON.parse(sessionData);
          if (parsedSession?.sessionToken) {
            headers['Authorization'] = `Bearer ${parsedSession.sessionToken}`;
          }
        } catch (e) {
          console.warn('Failed to parse session data:', e);
        }
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          email: normalizedEmail, 
          firstName, 
          lastName, 
          forceEmail  // Add forceEmail parameter to request body
        })
      });

      // If unauthorized or forbidden, try making a public request
      if (response.status === 401 || response.status === 403) {
        const publicHeaders = { 'Content-Type': 'application/json' };
        
        const publicResponse = await fetch(endpoint, {
          method: 'POST',
          headers: publicHeaders,
          body: JSON.stringify({ 
            email: normalizedEmail, 
            firstName, 
            lastName, 
            forceEmail  // Add forceEmail parameter to public retry as well
          })
        });
        
        const data = await publicResponse.json();
        return data;
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Magic link request failed:', error);
      throw error;
    }
  }

  async verifyToken(token, orgId) {
    try {
      // First try orgId from parameters
      let targetOrgId = orgId;
      
      // Then check URL parameters
      if (!targetOrgId) {
        targetOrgId = new URLSearchParams(window.location.search).get('orgId');
      }
      
      // Then check storage
      if (!targetOrgId) {
        targetOrgId = this.getOrganizationId();
      }
      
      // If still not found and we can extract email from token, try lookup
      if (!targetOrgId && token) {
        try {
          // Only attempt lookup if token contains encoded email (ml_*)
          if (token.startsWith('ml_')) {
            // Try to extract email from token for lookup
            // This is a simplified approach - the actual email extraction may differ
            // based on your token format
            const parts = token.split('_');
            if (parts.length >= 2) {
              const possibleEmail = decodeURIComponent(parts[1]);
              if (possibleEmail.includes('@')) {
                targetOrgId = await this.lookupOrgId(possibleEmail);
              }
            }
          }
        } catch (e) {
          console.warn('Failed to extract email from token for org lookup:', e);
        }
      }
      
      // Throw error if no orgId found
      if (!targetOrgId) {
        throw new Error('No organization ID found. Cannot verify token.');
      }
      
      // Use the cleaner approach with the global endpoint
      const response = await fetch(`${this.baseUrl}/org/${targetOrgId}/magic-links/verify`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          token
        })
      });

      if (!response.ok) {
        let errorMessage = `HTTP error ${response.status}`;
        
        try {
          const errorData = await response.json();
          
          if (errorData.error) {
            // Get details and format a more helpful error message
            const details = errorData.error;
            if (details.code === 'UNAUTHORIZED') {
              if (details.details?.reason === 'missing_header') {
                errorMessage = 'Authorization token is required but was not provided.';
              } else if (details.details?.reason === 'invalid_token') {
                errorMessage = 'The magic link token is invalid or has expired. Please request a new one.';
              } else {
                errorMessage = details.message || 'Authentication failed.';
              }
            } else {
              errorMessage = details.message || errorMessage;
            }
          } else {
            errorMessage = errorData.error || errorData.details || errorMessage;
          }
        } catch (e) {
          console.error('Error parsing error response:', e);
        }
        
        console.error('Token verification failed:', errorMessage);
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      // Log the raw API response for debugging without parsing
      console.log('Raw API response JSON:', JSON.stringify(result, null, 2));
      
      // Directly check if sessionToken exists in the response (camelCase)
      console.log('Does sessionToken exist in response?', 'sessionToken' in result);
      console.log('SessionToken value:', result.sessionToken);
      
      // Always use the camelCase version since that's what the API returns
      const sessionToken = result.sessionToken;
      
      if (!sessionToken) {
        console.error('No valid session token found in API response');
        console.log('API response keys:', Object.keys(result));
        return {
          is_valid: false,
          error: 'Authentication failed: No session token in response'
        };
      }
      
      console.log('Using session token:', sessionToken);
      
      // Store session data in session storage with expiry
      const expiresAt = result.expiresAt || result.expires_at || (Date.now() + 24 * 60 * 60 * 1000); // 24 hour default
      const sessionData = {
        sessionToken: sessionToken,
        email: result.user?.email,
        orgId: result.orgId || targetOrgId,
        userData: result.userData || result.user,
        expiresAt
      };
      
      console.log('Storing session data:', sessionData);
      sessionStorage.setItem('kablewy_session', JSON.stringify(sessionData));
      sessionStorage.setItem('kablewy_session_expiry', expiresAt.toString());
      
      // Return standardized format with consistent property names matching what our auth service expects
      return {
        is_valid: true,
        sessionToken: sessionToken, // This is the critical property that must match exactly
        email: result.user?.email,
        orgId: result.orgId || targetOrgId,
        userData: result.userData || result.user,
        expiresAt
      };
    } catch (error) {
      console.error('Verification error:', error);
      return {
        is_valid: false,
        error: this.handleError(error)
      };
    }
  }

  handleError(error) {
    let message = 'Invalid or expired login link. Please try again.';
    
    if (typeof error === 'string') {
      message = error;
    } else if (error instanceof Error) {
      message = error.message || 'Authentication error';
    } else if (error && typeof error === 'object') {
      message = error.error || error.message || error.details || 'Authentication error';
    }
    
    if (store?.setState) {
      store.setState({ error: message });
    }
    return message;
  }
}

export const magicLinkService = new MagicLinkService();